import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { absenceService, AbsenceResponse, AbsenceSearchRequest } from '../../services/absenceService';
import { classeService, Classe } from '../../services/classeService';
import { lessonService, LessonResponse } from '../../services/lessonService';
import { useAuth } from '../../context/AuthContext';
import { useRights } from '../../hooks/useRights';
import { useModal } from '../../context/ModalContext';
import { Trash2 } from 'lucide-react';
import '../../styles/index.css';
import './Absences.css';
import TableActions from '../../components/TableActions';

const Absences: React.FC = () => {
  const { t } = useTranslation();
  const { userType, user } = useAuth();
  const { hasRight } = useRights();
  const { showConfirm, showAlert } = useModal();

  const [filters, setFilters] = useState<AbsenceSearchRequest>({
    startDate: '',
    endDate: '',
    classId: '',
    lessonId: '',
    studentId: ''
  });

  // Add appliedFilters state to store the filters used for the current search results
  const [appliedFilters, setAppliedFilters] = useState<AbsenceSearchRequest>({
    startDate: '',
    endDate: '',
    classId: '',
    lessonId: '',
    studentId: ''
  });

  const [absences, setAbsences] = useState<AbsenceResponse[]>([]);
  const [classes, setClasses] = useState<Classe[]>([]);
  const [lessons, setLessons] = useState<LessonResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [hasSearched, setHasSearched] = useState(false);

  // Pagination state
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasMore, setHasMore] = useState(false); // Add hasMore state

  const observerTarget = useRef<HTMLDivElement>(null);

  const isTeacher = userType === 'TEACHER';
  const canDelete = hasRight('absences:d');

  useEffect(() => {
    loadClasses();
  }, []);

  useEffect(() => {
    if (filters.classId) {
      loadLessons(filters.classId);
    } else {
      setLessons([]);
    }
  }, [filters.classId]);

  const loadClasses = async () => {
    try {
      const allClasses = await classeService.getAllClasses();
      setClasses(allClasses);
    } catch (error) {
      console.error('Failed to load classes', error);
    }
  };

  const loadLessons = async (classId: string) => {
    try {
      let fetchedLessons: LessonResponse[] = [];
      if (isTeacher && user?.id) {
        const teacherLessons = await lessonService.getLessonsByTeacher(user.id);
        fetchedLessons = teacherLessons.filter(l => l.classeId === classId);
      } else {
        fetchedLessons = await lessonService.getLessonsByClass(classId);
      }
      setLessons(fetchedLessons);
    } catch (error) {
      console.error('Failed to load lessons', error);
    }
  };

  const performSearch = async (pageIndex: number, isNewSearch: boolean = false) => {
    if (isNewSearch) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      // Use appliedFilters for pagination (loading more), but filters for new search
      const searchFilters = isNewSearch ? filters : appliedFilters;

      const request: AbsenceSearchRequest = {
        ...searchFilters,
        page: pageIndex,
        size: 100
      };

      if (request.startDate) {
        request.startDate = new Date(request.startDate).toISOString();
      }
      if (request.endDate) {
        const date = new Date(request.endDate);
        date.setHours(23, 59, 59, 999);
        request.endDate = date.toISOString();
      }

      const pageResult = await absenceService.searchAbsences(request);

      if (isNewSearch) {
        setAbsences(pageResult.content);
        setAppliedFilters(filters); // Update applied filters on new search
      } else {
        setAbsences(prev => [...prev, ...pageResult.content]);
      }

      setTotalPages(pageResult.totalPages);
      setPage(pageIndex);
      setHasMore(pageIndex < pageResult.totalPages - 1);
      setHasSearched(true);
    } catch (error) {
      console.error('Search failed', error);
      showAlert(t('common.error'), t('common.error'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleSearch = () => {
    performSearch(0, true);
  };

  const loadMoreAbsences = useCallback(() => {
    if (!loadingMore && hasMore && !loading) {
      performSearch(page + 1, false);
    }
  }, [loadingMore, hasMore, loading, page, appliedFilters]); // Depend on appliedFilters instead of filters

  // Scroll Effect
  useEffect(() => {
    const currentTarget = observerTarget.current;
    if (!currentTarget) return;

    const handleScroll = () => {
      const container = currentTarget;
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      // Trigger when scrolled to within 100px of bottom
      if (scrollHeight - scrollTop - clientHeight < 100 && hasMore && !loadingMore && !loading) {
        loadMoreAbsences();
      }
    };

    currentTarget.addEventListener('scroll', handleScroll);
    return () => currentTarget.removeEventListener('scroll', handleScroll);
  }, [loadMoreAbsences, hasMore, loadingMore, loading]);

  const handleDelete = (id: string) => {
    showConfirm(t('common.delete'), t('absences.confirmDelete'), async () => {
      try {
        await absenceService.deleteAbsence(id);
        showAlert(t('common.success'), t('absences.deleted'));
        setAbsences(prev => prev.filter(a => a.id !== id));
      } catch (error) {
        console.error('Delete failed', error);
        showAlert(t('common.error'), t('absences.failedToDelete'));
      }
    });
  };

  const handleClearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      classId: '',
      lessonId: '',
      studentId: ''
    });
    setAppliedFilters({
      startDate: '',
      endDate: '',
      classId: '',
      lessonId: '',
      studentId: ''
    });
    setAbsences([]);
    setHasSearched(false);
    setPage(0);
    setTotalPages(0);
    setHasMore(false);
  };

  return (
    <div className="bo-container">
      <div className="bo-header">
        <div className="header-left">
          <button
            className="btn-toggle-sidebar"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? '◀' : '▶'}
          </button>
          <h2>{t('absences.title')}</h2>
        </div>
      </div>

      <div className="bo-content-wrapper">
        {showFilters && (
          <div className="bo-sidebar">
            <div className="sidebar-header">
              <h3>{t('common.filters')}</h3>
              <div className="sidebar-header-actions">
                <button className="btn-deep-search" onClick={handleSearch}>
                  {loading ? t('common.loading') : t('absences.search')}
                </button>
                <button className="btn-clear-filters" onClick={handleClearFilters}>
                  {t('common.clear')}
                </button>
              </div>
            </div>

            <div className="sidebar-body">
              <div className="filter-group">
                <label>{t('absences.fromDate')}</label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                />
              </div>

              <div className="filter-group">
                <label>{t('absences.toDate')}</label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                />
              </div>

              <div className="filter-group">
                <label>{t('absences.class')}</label>
                <select
                  value={filters.classId}
                  onChange={(e) => setFilters({ ...filters, classId: e.target.value })}
                >
                  <option value="">{t('absences.allClasses')}</option>
                  {classes.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>{t('absences.lesson')}</label>
                <select
                  value={filters.lessonId}
                  onChange={(e) => setFilters({ ...filters, lessonId: e.target.value })}
                  disabled={!filters.classId}
                >
                  <option value="">{t('absences.allLessons')}</option>
                  {lessons.map(l => (
                    <option key={l.id} value={l.id}>
                      {l.courseName} ({t(`timetable.days.${l.dayOfWeek}`)})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        <div className={`bo-main-content ${!showFilters ? 'full-width' : ''}`}>
          {!hasSearched ? (
            <div className="empty-state">
              <p>{t('common.search') + ' ' + t('common.actions').toLowerCase()}</p>
            </div>
          ) : (
            <div className="bo-table-container">
              <div className="bo-table-wrapper" ref={observerTarget}>
                {absences.length === 0 ? (
                  <div className="empty-state">
                    <p>{t('absences.noAbsencesFound')}</p>
                  </div>
                ) : (
                  <table className="bo-table">
                    <thead>
                      <tr>
                        <th>{t('absences.date')}</th>
                        <th>{t('absences.student')}</th>
                        <th>{t('absences.class')}</th>
                        <th>{t('absences.lesson')}</th>
                        <th>{t('absences.recordedBy')}</th>
                        {canDelete && <th>{t('common.actions')}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {absences.map(absence => (
                        <tr key={absence.id}>
                          <td>
                            {new Date(absence.period.start).toLocaleDateString()} {' '}
                            {new Date(absence.period.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td>{absence.studentName}</td>
                          <td>{absence.className}</td>
                          <td>
                            {absence.courseName} <br />
                            <small style={{ color: '#6b7280' }}>{absence.lessonTimeInfo}</small>
                          </td>
                          <td>{absence.recordedBy}</td>
                          {canDelete && (
                            <td>
                              <TableActions actions={[
                                {
                                  label: t('common.delete'),
                                  onClick: () => handleDelete(absence.id),
                                  className: 'btn-delete-small',
                                  icon: <Trash2 size={16} />,
                                  hideLabelDesktop: true,
                                  variant: 'danger'
                                }
                              ]} />
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {absences.length > 0 && (
                <div className="table-info">
                  {loadingMore && (
                    <span className="loading-more">{t('common.loading')}...</span>
                  )}
                  {!hasMore && absences.length > 0 && (
                    <span className="no-more-data">{t('common.endOfResults')}</span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Absences;
